import * as Sentry from '@sentry/react';

import { isNetworkApiError } from './isNetworkApiError';

export type Feature =
  | 'get-started'
  | 'create-location'
  | 'shared'
  | 'google-oauth'
  | 'sign-up'
  | 'login'
  | 'forgot-password'
  | 'reset-password'
  | 'verify-email'
  | 'add-location'
  | 'all-locations'
  | 'location'
  | 'location-summary'
  | 'location-switcher'
  | 'rankings'
  | 'local-search-grid'
  | 'local-rank-tracker'
  | 'ai-visibility'
  | 'review-tracker'
  | 'review-manager'
  | 'reviews'
  | 'logout'
  | 'errors'
  | 'google-map'
  | 'lsg-grid-points'
  | 'run-reports'
  | 'citations'
  | 'capabilities'
  | 'account-profile'
  | 'account-security'
  | 'labs';

/** Sentry severity levels accepted by `captureSentryException`. */
export type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug' | 'log';

interface CaptureSentryExceptionOptions {
  /** Message, which will allow to identify the error in Sentry */
  message: string;
  /** Feature, which will allow to identify the error placement in Sentry */
  feature: Feature;
  /** Level of the error */
  level?: SentryLevel;
  /** Optional: pass an Error or API error object. Error is captured as the exception */
  error?: unknown;
  /** Optional: pass extra data you believe will be useful for debugging the error */
  extra?: Record<string, unknown>;
  /** Optional: pass additional tags you believe will be useful for debugging the error */
  tags?: Record<string, string>;
  /**
   * Optional: overrides Sentry's default grouping so events with the same fingerprint land in
   * one issue and events with different fingerprints are kept apart (e.g. one issue per API
   * error reason instead of one issue per message).
   */
  fingerprint?: string[];
}

export const captureSentryException = ({
  message,
  feature,
  level = 'error',
  error,
  extra,
  tags,
  fingerprint
}: CaptureSentryExceptionOptions) => {
  // Network errors are skipped here as a backstop for the direct callers not yet migrated
  // to `meta.errorReporting` (NP-347) — `reportRequestError` already drops them in its
  // default policy. Remove once that migration completes.
  if (isNetworkApiError(error)) return;

  const exception = error instanceof Error ? error : new Error(message);

  Sentry.captureException(exception, (scope) => {
    scope.setTag('feature', feature);
    scope.setTag('message', message);
    scope.setLevel(level);
    if (error !== undefined && !(error instanceof Error)) {
      scope.setExtra('error', error);
    }
    if (extra !== undefined) scope.setExtras(extra);
    if (tags !== undefined) scope.setTags(tags);
    if (fingerprint !== undefined) scope.setFingerprint(fingerprint);
    return scope;
  });
};

interface CaptureSchemaValidationErrorOptions {
  /** API-client method, which will allow to identify the error placement in Sentry */
  method: string;
  /** Feature, which will allow to identify the error placement in Sentry */
  feature: Feature;
  /** Optional: pass an Error or API error object. Error is captured as the exception */
  error?: unknown;
  /** Optional: pass extra data you believe will be useful for debugging the error */
  extra?: Record<string, unknown>;
}

interface SetSentryUserOptions {
  /** Authenticated user's unique id */
  userId: string;
  /** Id of the customer (account) the user belongs to */
  customerId: string;
  /** Subscription package id — lets us correlate errors with a specific plan */
  packageId: string;
  /** Optional customer display name */
  customerName?: string;
}

/** Sets the user identity in Sentry. */
export const setSentryUser = ({ userId, customerId, packageId, customerName }: SetSentryUserOptions) => {
  Sentry.setUser({ id: userId });
  Sentry.setTag('customerId', customerId);
  Sentry.setTag('packageId', packageId);
  Sentry.setTag('customerName', customerName ?? null);
};

/** Clears user identity from Sentry (call on logout). */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag('customerId', null);
  Sentry.setTag('packageId', null);
  Sentry.setTag('customerName', null);
};

export const captureSchemaValidationError = ({
  feature,
  error,
  method,
  extra
}: CaptureSchemaValidationErrorOptions) => {
  /** Type guard to check if the error is a schema validation error */
  const schemaError = error as { error?: { issues?: unknown } } | undefined;
  /** Extract the Zod issues from the schema error */
  const zodIssues = schemaError?.error?.issues;

  captureSentryException({
    message: 'API response validation failed: response did not match expected schema',
    feature,
    level: 'error',
    error,
    extra: {
      method,
      ...(zodIssues !== undefined && { zodIssues: JSON.stringify(zodIssues) }),
      ...extra
    },
    tags: {
      api: 'schema-validation-error'
    }
  });
};

/** Add a lightweight Sentry breadcrumb for auth-state transitions. */
export const addSentryAuthBreadcrumb = (message: string, feature: Feature, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    category: feature,
    type: 'info',
    level: 'info',
    message,
    data,
    timestamp: Date.now() / 1000
  });
};
